import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Search, ChevronDown, ChevronRight, Eye, MousePointer, Clock, User } from "lucide-react";
import { UserActivity } from "@/hooks/useAdminUserActivity";
import { format, formatDistanceToNow } from "date-fns";
import { useEventTitles } from "@/hooks/useEventTitles";
import { isEventPath, extractEventIdFromPath } from "@/lib/analytics";
import { EventPreviewDialog } from "./EventPreviewDialog";

interface UserActivityTableProps {
  data: UserActivity[] | undefined;
  isLoading: boolean;
}

const ITEMS_PER_PAGE = 15;

export const UserActivityTable = ({ data, isLoading }: UserActivityTableProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  // Collect all pages from all users to resolve event titles
  const allPaths = useMemo(() => {
    if (!data) return [];
    return data.flatMap((user) => user.pages_list);
  }, [data]);

  const { data: eventTitles = {} } = useEventTitles(allPaths);

  const formatPageDisplay = (page: string): string => {
    const eventId = extractEventIdFromPath(page);
    if (eventId && eventTitles[eventId]) {
      const title = eventTitles[eventId];
      return title.length > 20 ? `${title.slice(0, 20)}...` : title;
    }
    return page.length > 25 ? `${page.slice(0, 25)}...` : page;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>User Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const filteredData = (data || []).filter(
    (user) =>
      user.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.user_email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const toggleRow = (key: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedRows(newExpanded);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              User Activity
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {filteredData.length} users tracked
            </p>
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>User</TableHead>
                <TableHead className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Eye className="h-3.5 w-3.5" />
                    Pages
                  </div>
                </TableHead>
                <TableHead className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <MousePointer className="h-3.5 w-3.5" />
                    Clicks
                  </div>
                </TableHead>
                <TableHead className="text-center">Sessions</TableHead>
                <TableHead>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Last Active
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No user activity found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((user) => {
                  const rowKey = user.user_id || user.user_email;
                  const isExpanded = expandedRows.has(rowKey);

                  return (
                    <Collapsible key={rowKey} asChild>
                      <>
                        <TableRow className="h-12">
                          <TableCell className="p-2">
                            <CollapsibleTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => toggleRow(rowKey)}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                            </CollapsibleTrigger>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-sm">
                                {user.user_name}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {user.user_email}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center">
                              <span className="font-semibold">{user.total_page_views}</span>
                              <span className="text-xs text-muted-foreground">
                                {user.unique_pages_viewed} unique
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-semibold">{user.total_clicks}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{user.session_count}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="text-sm">
                                {formatDistanceToNow(new Date(user.last_active_at), { addSuffix: true })}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(user.last_active_at), "MMM d, HH:mm")}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                        <CollapsibleContent asChild>
                          <TableRow className="bg-muted/30">
                            <TableCell colSpan={6} className="p-4">
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                                    <Eye className="h-4 w-4" />
                                    Pages Viewed
                                  </h4>
                                <div className="flex flex-wrap gap-1">
                                    {user.pages_list.length > 0 ? (
                                      user.pages_list.map((page, idx) => {
                                        const eventId = extractEventIdFromPath(page);
                                        const isEvent = isEventPath(page);
                                        
                                        if (isEvent && eventId) {
                                          return (
                                            <Badge
                                              key={idx}
                                              variant="outline"
                                              className="text-xs cursor-pointer hover:bg-primary/10 transition-colors"
                                              onClick={() => setSelectedEventId(eventId)}
                                              title={eventTitles[eventId] || page}
                                            >
                                              {formatPageDisplay(page)}
                                            </Badge>
                                          );
                                        }
                                        
                                        return (
                                          <Badge key={idx} variant="outline" className="text-xs" title={page}>
                                            {formatPageDisplay(page)}
                                          </Badge>
                                        );
                                      })
                                    ) : (
                                      <span className="text-xs text-muted-foreground">No pages tracked</span>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                                    <MousePointer className="h-4 w-4" />
                                    Buttons Clicked
                                  </h4>
                                  <div className="flex flex-wrap gap-1">
                                    {user.buttons_clicked.length > 0 ? (
                                      user.buttons_clicked.map((btn, idx) => (
                                        <Badge key={idx} variant="secondary" className="text-xs">
                                          {btn}
                                        </Badge>
                                      ))
                                    ) : (
                                      <span className="text-xs text-muted-foreground">No clicks tracked</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="mt-3 text-xs text-muted-foreground">
                                First seen: {format(new Date(user.first_seen_at), "MMM d, yyyy HH:mm")}
                              </div>
                            </TableCell>
                          </TableRow>
                        </CollapsibleContent>
                      </>
                    </Collapsible>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <EventPreviewDialog
        eventId={selectedEventId}
        open={!!selectedEventId}
        onOpenChange={(open) => !open && setSelectedEventId(null)}
      />
    </Card>
  );
};
